import React, { useEffect, useMemo } from "react";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { UpIcon, DownIcon, CheckIcon } from "@directv-digital/fusion.atoms.icons";

export const DtvCompetitorComparisonTableHead = React.memo((props: any) => {
  const {
    featureColWidth,
    isGenrePack,
    dtvColWidth,
    competitorColWidth,
    mainPackageLogo,
    mainPackageLabel,
    mainPackages,
    selectedMainPackage,
    mainSelectOpen,
    setMainSelectOpen,
    handleMainPackageChange,
    tooltip,
    aria,
    competitors,
    competitorKeys,
    selectedCompetitor,
    selectRef,
    selectInputRef,
    selectOpen,
    setSelectOpen,
    handleCompetitorChange
  } = props;
  const showMainPackageDropdown = !!isGenrePack && (mainPackages?.length || 0) > 1;

  // Prefetch competitor logos
  useEffect(() => {
    competitorKeys?.forEach((key: string) => {
      const logo = competitors?.[key]?.logo;
      if (logo) {
        const img = new Image();
        img.src = logo;
      }
    });
  }, [competitorKeys, competitors]);

  // Memoize menu items so Select open does not trigger full re-render cost each time
  const competitorMenuItems = useMemo(() => (
    competitorKeys?.map((key: string) => {
      const competitor = competitors?.[key];
      return (
        <MenuItem
          aria-label={key}
          key={key}
          value={key}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            minWidth: 0,
            padding: "8px 12px",
            position: "relative"
          }}
        >
          {competitor?.logo ? (
            <DtvBox
              alt={key}
              aria-label={`${competitor?.label || key} logo`}
              component="img"
              src={competitor?.logo}
              sx={{
                width: 56,
                height: 28,
                display: "block",
                objectFit: "contain",
                my: "auto",
                ...(competitor?.logoStyle || {})
              }}
            />
          ) : (
            <DtvText
              content={competitor?.label || key}
              style={{
 fontWeight: 500,
fontSize: 16,
textAlign: "center"
}}
            />
          )}
          {key === selectedCompetitor && (
            <CheckIcon
              aria-hidden="true"
              fill="#1E815D"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 20,
                height: 20
              }}
            />
          )}
        </MenuItem>
      );
    })
  ), [competitorKeys, competitors, selectedCompetitor]);

  return (
    <TableHead>
      <TableRow>
        {/* Feature column (empty header) */}
        <TableCell
          aria-hidden="true"
          sx={{
            width: featureColWidth,
            minWidth: featureColWidth,
            maxWidth: featureColWidth,
            background: "transparent",
            padding: "8px 12px",
            boxSizing: "border-box",
            verticalAlign: "middle"
          }}
        />
        {/* DIRECTV main package column header */}
        <TableCell
          aria-label={aria?.dtvColLabel}
          scope="col"
          sx={{
            width: dtvColWidth,
            minWidth: dtvColWidth,
            maxWidth: dtvColWidth,
            borderStyle: "solid",
            borderWidth: "5px 5px 0 5px",
            borderImage: "linear-gradient(to right, #006DFF, #00F0FF) 1",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            backgroundColor: "#FBFBFB !important",
            position: "relative",
            boxSizing: "border-box",
            padding: {
              xs: "12px 12px",
              sm: isGenrePack ? "24px 12px 8px 12px" : "8px 12px"
            },
            verticalAlign: "middle",
            textAlign: "center"
          }}
        >
          <DtvBox
            alt="DIRECTV"
            component="img"
            src={mainPackageLogo}
            sx={{
              width: {
 xs: isGenrePack ? 90 : 72,
sm: 113
},
              height: {
 xs: isGenrePack ? 18 : 14,
sm: 22
},
              marginBottom: 2,
              display: "block",
              marginLeft: "auto",
              marginRight: "auto"
            }}
          />
          {isGenrePack && (
            <DtvBox sx={{
 display: "flex",
alignItems: "center",
justifyContent: "center",
position: "relative",
padding: "5px"
}}>
              {showMainPackageDropdown ? (
                <FormControl
                  aria-label={aria?.dtvPackageSelector}
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: {
 xs: "109px",
sm: "137px",
md: "137px"
},
                    maxWidth: {
 xs: "109px",
sm: "137px",
md: "137px"
},
                    height: {
 xs: "31px",
sm: "27px",
md: "27px"
}
                  }}
                >
                  <span id="main-package-label" style={{
 position: "absolute",
width: 1,
height: 1,
padding: 0,
margin: -1,
overflow: "hidden",
clip: "rect(0,0,0,0)",
border: 0
}}>
                    {aria?.dtvPackageSelector}
                  </span>
                  <Select
                    labelId="main-package-label"
                    open={!!mainSelectOpen}
                    value={selectedMainPackage}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          width: {
 xs: "109px",
sm: "137px",
md: "137px"
},
                          minWidth: {
 xs: "109px",
sm: "137px",
md: "137px"
},
                          maxWidth: {
 xs: "109px",
sm: "137px",
md: "137px"
},
                          boxShadow: 3,
                          overflow: "hidden"
                        }
                      },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left"
                      }
                    }}
                    inputProps={{
                      'aria-label': aria?.dtvPackageSelector,
                      'aria-describedby': 'dtv-package-selector-desc'
                    }}
                    renderValue={() => {
                      const pkg = mainPackages?.find((p: any) => p.key === selectedMainPackage);
                      return (
                        <DtvText
                          content={pkg?.label || ''}
                          sx={{
 fontWeight: "bold",
fontSize: {
 xs: "11px !important",
sm: "13px !important"
},
textAlign: "center",
marginBottom: {
 xs: "12px",
sm: "0px"
}
}}
                        />
                      );
                    }}
                    sx={{
                      width: {
 xs: "109px",
sm: "137px",
md: "137px"
},
                      minWidth: {
 xs: "109px",
sm: "137px",
md: "137px"
},
                      maxWidth: {
 xs: "109px",
sm: "137px",
md: "137px"
},
                      height: {
 xs: "31px",
sm: "27px",
md: "27px"
},
                      borderRadius: "4px",
                      backgroundColor: "#FBFBFB !important",
                      "& .MuiSelect-icon": {
                        opacity: 0
                      },
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        backgroundColor: "#FBFBFB !important",
                        paddingRight: {
 xs: "14px !important",
sm: "32px !important"
}
                      }
                    }}
                    onChange={handleMainPackageChange}
                    onClose={() => setMainSelectOpen(false)}
                    onOpen={() => setMainSelectOpen(true)}
                  >
                    {mainPackages?.map((pkg: any) => (
                      <MenuItem
                        key={pkg.key}
                        value={pkg.key}
                        sx={{
                          backgroundColor: "#FBFBFB !important",
                          fontSize: {
 xs: "11px !important",
sm: "12px !important"
},
minHeight: 30,
paddingLeft: {
 xs: "5px",
sm: "8px"
},
position: "relative"
                        }}
                      >
                        {pkg.label}
                        {pkg.key === selectedMainPackage && (
                          <CheckIcon
                            aria-hidden="true"
                            fill="#1E815D"
                            style={{
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: 20,
                              height: 20
                            }}
                          />
                        )}
                      </MenuItem>
                    ))}

                  </Select>
                  {/* Arrow below label for mobile only */}
                  <DtvBox
                    sx={{
                      width: "100%",
                      display: {
 xs: "flex",
sm: "none"
},
                      justifyContent: "center",
                      alignItems: "center",
                      top: "14px",
                      position: "absolute"
                    }}
                    onClick={() => setMainSelectOpen(true)}

                  >
                    {mainSelectOpen ? (
                      <UpIcon fill="#006DFF" style={{
 width: 18,
height: 18
}} />
                    ) : (
                      <DownIcon fill="#006DFF" style={{
 width: 18,
height: 18
}} />
                    )}
                  </DtvBox>

                  {/* Arrow at right for desktop/tablet only */}
                  <DtvBox
                    sx={{
                      pointerEvents: "none",
                      position: "absolute",
                      right: {
 xs: 0,
sm: "5px",
md: "12px"
},
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      display: {
 xs: "none",
sm: "flex"
},
                      alignItems: "center",
                      height: "100%"
                    }}
                  >
                    {mainSelectOpen ? (
                      <UpIcon fill="#006DFF" style={{
 width: 15,
height: 15
}} />
                    ) : (
                      <DownIcon fill="#006DFF" style={{
 width: 15,
height: 15
}} />
                    )}
                  </DtvBox>
                  <span id="dtv-package-selector-desc" style={{ display: "none" }}>
                    {aria?.dtvPackageSelectorDesc}
                  </span>
                </FormControl>
              ) : (
                <DtvText
                  aria-label={aria?.dtvColLabel}
                  content={mainPackageLabel}
                  variant="body3medium"
                  sx={{
                    fontSize: {
 xs: "13px",
sm: "14px"
},
fontWeight: "bold",
                    ...(tooltip?.enabled && {
 paddingRight: {
 xs: "2px",
sm: "5px"
}
})
                  }}
                />
              )}
              {(tooltip?.enabled && tooltip?.content) && (
                <Tooltip
                  aria-label={tooltip?.content}
                  closeOnScroll={true}
                  content={tooltip?.content}
                  placement="top"
                  PopperProps={{
                    sx: {
                      "& .MuiTypography-root": {
                        fontFamily: "roboto-regular",
                        fontSize: "14px"
                      }
                    }
                  }}
                />
              )}
            </DtvBox>)}
        </TableCell>
        {/* Competitor column header */}
        <TableCell
          aria-label={aria?.competitorColLabel}
          scope="col"
          sx={{
            width: competitorColWidth,
            minWidth: competitorColWidth,
            maxWidth: competitorColWidth,
            background: "transparent",
            position: "relative",
            padding: {
 xs: "0 8px",
sm: "17px 17px"
},
            boxSizing: "border-box",
            verticalAlign: "middle"
          }}
        >
          <DtvBox
            style={{
              width: "100%",
              maxWidth: "100vw",
              overflowX: "hidden",
              boxSizing: "border-box"
            }}
          >
            <FormControl
              aria-label={aria?.selectCompetitor}
              ref={selectRef}
              size="small"
              variant="outlined"
              sx={{
                width: {
 xs: "103px",
sm: "175px",
md: "225px"
},
                maxWidth: {
 xs: "103px",
sm: "175px",
md: "225px"
}
              }}
            >
              <span id="competitor-label" style={{
 position: "absolute",
width: 1,
height: 1,
padding: 0,
margin: -1,
overflow: "hidden",
clip: "rect(0,0,0,0)",
border: 0
}}>
                {aria?.competitorSelector}
              </span>
              <Select
                inputRef={selectInputRef}
                labelId="competitor-label"
                open={!!selectOpen}
                value={selectedCompetitor}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      width: {
 xs: "103px",
sm: "175px",
md: "225px"
},
                      minWidth: {
 xs: "103px",
sm: "175px",
md: "225px"
},
                      maxWidth: {
 xs: "103px",
sm: "175px",
md: "225px"
},
                      borderRadius: 2,
                      boxShadow: 3,
                      overflow: "hidden"
                    }
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  }
                }}
                inputProps={{
                  'aria-label': aria?.competitorSelector,
                  'aria-describedby': 'competitor-selector-desc'
                }}
                renderValue={() => {
                  const competitor = competitors?.[selectedCompetitor];
                  return competitor?.logo ? (
                    <DtvBox
                      alt={selectedCompetitor}
                      aria-label={`${competitor?.label || selectedCompetitor} logo`}
                      component="img"
                      src={competitor?.logo}
                      sx={{
 width: 56,
height: 28,
display: "block",
...(competitor?.logoStyle || {})
}}
                    />
                  ) : (
                    <DtvText
                      content={competitor?.label || selectedCompetitor}
                      style={{
 fontWeight: 500,
fontSize: 16,
textAlign: "center"
}}
                    />
                  );
                }}
                sx={{
                  width: {
 xs: "103px",
sm: "175px",
md: "225px"
},
                  maxWidth: {
 xs: "103px",
sm: "175px",
md: "225px"
},
                  "& .MuiSelect-icon": {
                    opacity: 0
                  }
                }}
                onChange={handleCompetitorChange}
                onClose={() => setSelectOpen(false)}
                onOpen={() => setSelectOpen(true)}
              >
                {competitorMenuItems}
              </Select>
              <DtvBox
                sx={{
                  pointerEvents: "none",
                  position: "absolute",
                  right: {
 xs: "6px",
sm: "12px"
},
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  height: "100%"
                }}
              >
                {selectOpen ? (
                  <UpIcon fill="#006DFF" style={{
 width: 15,
height: 15
}} />
                ) : (
                  <DownIcon fill="#006DFF" style={{
 width: 15,
height: 15
}} />
                )}
              </DtvBox>
              <span id="competitor-selector-desc" style={{ display: "none" }}>
                {aria?.competitorSelectorDesc}
              </span>
            </FormControl>
          </DtvBox>
        </TableCell>
      </TableRow>
    </TableHead>
  );
});
